import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2, Package, Store } from "lucide-react";
import { toast } from "react-toastify";
import { getPublicAffiliateLink } from "../services/ProductsService";
import {
  getProductRecommendations,
  getProductRecommendationsQueryKey,
  type ProductRecommendation,
  type ProductRecommendationsRequest,
} from "../services/ProductRecommendationsService";
import { getProductImageUrl } from "../utils/imageUtils";
import { track } from "../utils/analytics";

interface ProductRecommendationsProps {
  sku: string;
  storeId: string;
  retailer: string;
}

const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

function RecommendationCard({
  item,
  rank,
  sourceRetailer,
}: {
  item: ProductRecommendation;
  rank: number;
  sourceRetailer: string;
}) {
  const [opening, setOpening] = useState(false);
  const matchLabel =
    item.matchType === "exact"
      ? "Same product"
      : item.matchType === "similar"
        ? "Similar"
        : "Related";

  const handleOpen = async () => {
    if (opening) return;
    const tab = window.open("about:blank", "_blank");
    setOpening(true);
    track("product_recommendation_clicked", {
      surface: "product_detail_modal",
      source_retailer: sourceRetailer,
      retailer: item.retailer,
      match_type: item.matchType,
      rank,
    });
    try {
      const url = await getPublicAffiliateLink(item.productKey);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch {
      if (tab) tab.close();
      toast.error("Could not open this retailer right now");
    } finally {
      setOpening(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleOpen}
      disabled={opening}
      className="group flex min-w-0 items-center gap-3 rounded-xl border border-gray-100 bg-white p-2.5 text-left transition-all hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-sm active:translate-y-0 disabled:opacity-60 dark:border-dark-700 dark:bg-dark-900 dark:hover:border-purple-800"
      aria-label={`Open ${item.name} at ${item.retailerName}`}
    >
      <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-white dark:border-dark-700">
        {item.image ? (
          <img
            src={getProductImageUrl(item.image, "150")}
            alt=""
            loading="lazy"
            className="h-full w-full object-contain"
          />
        ) : (
          <Package className="h-5 w-5 text-gray-300 dark:text-dark-600" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-1.5">
          <span className="truncate text-[10px] font-extrabold uppercase tracking-wide text-purple-700 dark:text-purple-300">
            {item.retailerName}
          </span>
          <span
            className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
              item.matchType === "exact"
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                : "bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300"
            }`}
          >
            {matchLabel}
          </span>
        </div>
        <p className="line-clamp-2 text-xs font-semibold leading-snug text-gray-800 group-hover:text-purple-700 dark:text-dark-100 dark:group-hover:text-purple-300">
          {item.name}
        </p>
        <div className="mt-1 flex items-baseline gap-1.5">
          <span className="text-sm font-extrabold tabular-nums text-emerald-600 dark:text-emerald-400">
            {priceFormatter.format(item.currentPrice)}
          </span>
          {item.discountPercent > 0 && (
            <span className="text-[10px] font-bold text-red-500 dark:text-red-400">
              {item.discountPercent}% off
            </span>
          )}
        </div>
      </div>
      {opening ? (
        <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-purple-500" />
      ) : (
        <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 transition-colors group-hover:text-purple-500 dark:text-dark-500" />
      )}
    </button>
  );
}

function RecommendationGroup({
  title,
  items,
  offset,
  sourceRetailer,
}: {
  title: string;
  items: ProductRecommendation[];
  offset: number;
  sourceRetailer: string;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-dark-400">
        {title}
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((item, index) => (
          <RecommendationCard
            key={`${item.retailer}-${item.productKey}`}
            item={item}
            rank={offset + index + 1}
            sourceRetailer={sourceRetailer}
          />
        ))}
      </div>
    </div>
  );
}

export default function ProductRecommendations({
  sku,
  storeId,
  retailer,
}: ProductRecommendationsProps) {
  const request = useMemo<ProductRecommendationsRequest>(
    () => ({ sku, storeId, retailer, limit: 6 }),
    [retailer, sku, storeId]
  );
  const impressionKeyRef = useRef("");
  const query = useQuery({
    queryKey: getProductRecommendationsQueryKey(request),
    queryFn: () => getProductRecommendations(request),
    enabled: Boolean(sku && storeId && retailer),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    refetchInterval: (state) => (state.state.data?.warming ? 1500 : false),
  });

  const items = query.data?.items ?? [];
  const exact = items.filter((item) => item.matchType === "exact");
  const alternatives = items.filter((item) => item.matchType !== "exact");

  useEffect(() => {
    if (items.length === 0) return;
    const impressionKey = `${retailer}:${storeId}:${sku}:${query.data?.generatedAt ?? 0}`;
    if (impressionKeyRef.current === impressionKey) return;
    impressionKeyRef.current = impressionKey;
    track("product_recommendations_viewed", {
      surface: "product_detail_modal",
      source_retailer: retailer,
      result_count: items.length,
    });
  }, [items.length, query.data?.generatedAt, retailer, sku, storeId]);

  if (query.isError) return null;
  if (query.isLoading || query.data?.warming) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-purple-100 bg-purple-50/60 px-3 py-2.5 text-xs font-medium text-purple-700 dark:border-purple-900/50 dark:bg-purple-950/20 dark:text-purple-300">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Finding similar deals across other retailers…
      </div>
    );
  }
  if (items.length === 0) return null;

  return (
    <section className="space-y-3 rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50/70 to-white p-3 dark:border-purple-900/50 dark:from-purple-950/20 dark:to-dark-800">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
            <Store className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-extrabold text-gray-900 dark:text-white">
              Deals like this from other retailers
            </h3>
            <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-400">
              Fresh matches found across DealHawk
            </p>
          </div>
        </div>
        <span className="flex-shrink-0 rounded-full bg-white px-2 py-1 text-[10px] font-bold text-purple-600 shadow-sm dark:bg-dark-900 dark:text-purple-300">
          {items.length} {items.length === 1 ? "retailer" : "retailers"}
        </span>
      </div>
      <RecommendationGroup
        title="Same product elsewhere"
        items={exact}
        offset={0}
        sourceRetailer={retailer}
      />
      <RecommendationGroup
        title={exact.length > 0 ? "Similar and related deals" : "Related deals"}
        items={alternatives}
        offset={exact.length}
        sourceRetailer={retailer}
      />
    </section>
  );
}
